<template>
  <b-modal
    :active.sync="showModal"
    trap-focus
    aria-role="dialog"
    :width="1000"
    aria-modal
    has-modal-card
    @close="closeModal"
  >
    <div class="modal-card" style="width: 1000px">
      <header class="modal-card-head">
        <p class="modal-card-title">Recent Changes</p>
      </header>
      <section
        class="modal-card-body"
        style="max-height: 600px; overflow-y: auto"
      >
        <div v-if="loading" class="has-text-centered">
          <b-loading :active="loading" :is-full-page="false"></b-loading>
        </div>

        <div
          v-else-if="changes.length === 0"
          class="has-text-centered has-text-grey"
        >
          <p>No recent changes found</p>
        </div>

        <div v-else style="background: white;">
          <table
            class="table is-striped is-fullwidth simple-table"
            style="color: #363636 !important;"
          >
            <thead>
              <tr style="background: #f5f5f5;">
                <th style="color: #363636 !important; width: 40px;"></th>
                <th style="color: #363636 !important;">Date/Time</th>
                <th style="color: #363636 !important;">User</th>
                <th style="color: #363636 !important;">Action</th>
                <th style="color: #363636 !important;">Type</th>
                <th style="color: #363636 !important;">Summary</th>
                <th style="color: #363636 !important;">Actions</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="change in changes">
                <tr :key="change.id" style="color: #363636 !important;">
                  <td style="color: #363636 !important; width: 40px;">
                    <button
                      class="button is-small"
                      style="color: #363636 !important; background: transparent; border: none; padding: 0;"
                      @click="toggleDetails(change.id)"
                    >
                      <span class="icon">
                        <i
                          class="mdi"
                          :class="
                            expandedRows.includes(change.id)
                              ? 'mdi-chevron-down'
                              : 'mdi-chevron-right'
                          "
                          style="color: #f14668 !important; font-size: 20px;"
                        ></i>
                      </span>
                    </button>
                  </td>
                  <td style="color: #363636 !important;">
                    {{ formatDateTime(change.created_at) }}
                  </td>
                  <td style="color: #363636 !important;">
                    <div v-if="change.user">
                      <strong style="color: #363636 !important;">{{
                        change.user.name
                      }}</strong
                      ><br />
                      <small style="color: #7a7a7a !important;">{{
                        change.user.email
                      }}</small>
                    </div>
                    <div v-else style="color: #b5b5b5 !important;">
                      Unknown User
                    </div>
                  </td>
                  <td style="color: #363636 !important;">
                    <span
                      class="tag"
                      :class="{
                        'is-success': change.action === 'create' || change.action === 'sequence_restore' || change.action === 'batch_copy' || change.action === 'batch_undo_delete',
                        'is-info': change.action === 'update' || change.action === 'batch_replace' || change.action === 'batch_undo_replace',
                        'is-danger': change.action === 'delete' || change.action === 'sequence_delete' || change.action === 'batch_delete' || change.action === 'batch_undo_copy'
                      }"
                      style="color: white !important;"
                    >
                      {{ formatAction(change.action) }}
                    </span>
                  </td>
                  <td style="color: #363636 !important;">
                    {{ formatTableName(change.table_name) }}
                  </td>
                  <td style="color: #363636 !important; font-size: 13px;">
                    <!-- Batch operations (delete, replace, copy playlist) -->
                    <div
                      v-if="change.table_name === 'batch' && change.enriched_info && change.enriched_info.is_batch"
                    >
                      <strong style="color: #363636 !important;">
                        {{ change.enriched_info.channel_name || 'Unknown Channel' }}
                      </strong>
                      <br />
                      <!-- Batch Delete -->
                      <span
                        v-if="change.action === 'batch_delete'"
                        style="color: #f14668 !important; font-size: 12px; font-style: italic;"
                      >
                        <i class="mdi mdi-delete-sweep" style="margin-right: 4px;"></i>
                        Deleted {{ change.enriched_info.item_count }} items
                        <span v-if="change.enriched_info.child_count > 0">
                          (+ {{ change.enriched_info.child_count }} children)
                        </span>
                      </span>
                      <!-- Batch Replace -->
                      <span
                        v-else-if="change.action === 'batch_replace'"
                        style="color: #3273dc !important; font-size: 12px; font-style: italic;"
                      >
                        <i class="mdi mdi-swap-horizontal" style="margin-right: 4px;"></i>
                        Replaced {{ change.enriched_info.item_count }} items with "{{ (change.enriched_info.new_media && change.enriched_info.new_media.title) || 'Unknown' }}"
                      </span>
                      <!-- Batch Copy -->
                      <span
                        v-else-if="change.action === 'batch_copy'"
                        style="color: #48c774 !important; font-size: 12px; font-style: italic;"
                      >
                        <i class="mdi mdi-content-copy" style="margin-right: 4px;"></i>
                        Copied {{ change.enriched_info.item_count }} items from {{ change.enriched_info.source_date }} to {{ change.enriched_info.destination_date }}
                      </span>
                      <!-- Batch Undo operations -->
                      <span
                        v-else-if="change.action === 'batch_undo_delete'"
                        style="color: #48c774 !important; font-size: 12px; font-style: italic;"
                      >
                        <i class="mdi mdi-undo" style="margin-right: 4px;"></i>
                        Restored {{ change.enriched_info.item_count }} deleted items
                      </span>
                      <span
                        v-else-if="change.action === 'batch_undo_replace'"
                        style="color: #3273dc !important; font-size: 12px; font-style: italic;"
                      >
                        <i class="mdi mdi-undo" style="margin-right: 4px;"></i>
                        Reverted {{ change.enriched_info.item_count }} replaced items
                      </span>
                      <span
                        v-else-if="change.action === 'batch_undo_copy'"
                        style="color: #f14668 !important; font-size: 12px; font-style: italic;"
                      >
                        <i class="mdi mdi-undo" style="margin-right: 4px;"></i>
                        Removed {{ change.enriched_info.item_count }} copied items
                      </span>
                    </div>

                    <!-- Sequence operations (delete, copy, etc.) -->
                    <div
                      v-else-if="change.table_name === 'sequence' && change.enriched_info && change.enriched_info.is_sequence"
                    >
                      <strong style="color: #363636 !important;">{{
                        (change.media_info && change.media_info.title) || 'Unknown'
                      }}</strong>
                      <span
                        v-if="change.media_info && change.media_info.series_no"
                        style="color: #7a7a7a !important;"
                      >
                        - Season {{ change.media_info.series_no }}
                      </span>
                      <br />
                      <span
                        v-if="change.action === 'sequence_delete'"
                        style="color: #f14668 !important; font-size: 12px; font-style: italic;"
                      >
                        <i class="mdi mdi-calendar-remove" style="margin-right: 4px;"></i>
                        Deleted sequence ({{ change.enriched_info.parent_item_count }} episodes, {{ change.enriched_info.total_item_count }} total items)
                      </span>
                      <span
                        v-else-if="change.action === 'sequence_restore'"
                        style="color: #48c774 !important; font-size: 12px; font-style: italic;"
                      >
                        <i class="mdi mdi-calendar-check" style="margin-right: 4px;"></i>
                        Restored sequence ({{ change.enriched_info.parent_item_count }} episodes)
                      </span>
                    </div>

                    <!-- Media info for schedules -->
                    <div
                      v-else-if="
                        change.table_name === 'schedules' && change.media_info
                      "
                    >
                      <strong style="color: #363636 !important;">{{
                        change.media_info.title
                      }}</strong>
                      <span
                        v-if="
                          change.media_info.type === 'series' &&
                            change.media_info.series_no &&
                            change.media_info.episode_number
                        "
                        style="color: #7a7a7a !important;"
                      >
                        - S{{ change.media_info.series_no }}E{{
                          change.media_info.episode_number
                        }}
                      </span>
                      <br />
                      <span
                        v-if="change.media_info.episode_title"
                        style="color: #7a7a7a !important; font-size: 12px;"
                      >
                        {{ change.media_info.episode_title }}
                      </span>
                      <br />
                      <span
                        v-if="change.action === 'update'"
                        style="color: #7a7a7a !important; font-size: 12px; font-style: italic;"
                      >
                        Updated
                        {{ Object.keys(change.old_values || {}).length }}
                        field(s)
                      </span>
                      <span
                        v-else-if="change.action === 'delete'"
                        style="color: #f14668 !important; font-size: 12px; font-style: italic;"
                      >
                        Deleted from schedule
                      </span>
                      <span
                        v-else-if="change.action === 'create'"
                        style="color: #48c774 !important; font-size: 12px; font-style: italic;"
                      >
                        Added to schedule
                      </span>
                    </div>

                    <!-- For non-schedule items -->
                    <div v-else>
                      <span v-if="change.action === 'update'">
                        Updated
                        {{ Object.keys(change.old_values || {}).length }}
                        field(s)
                      </span>
                      <span v-else-if="change.action === 'delete'">
                        Deleted {{ formatTableName(change.table_name) }}
                      </span>
                      <span v-else-if="change.action === 'create'">
                        Created {{ formatTableName(change.table_name) }}
                      </span>
                    </div>
                  </td>
                  <td style="color: #363636 !important;">
                    <button
                      v-if="!change.reverted"
                      class="button is-warning is-small"
                      style="color: white !important;"
                      :disabled="undoingId === change.id"
                      @click="confirmUndo(change)"
                    >
                      <span class="icon is-small">
                        <i class="mdi mdi-undo"></i>
                      </span>
                      <span>Undo</span>
                    </button>
                    <span
                      v-else
                      class="tag is-light"
                      style="color: #363636 !important;"
                      >Reverted</span
                    >
                  </td>
                </tr>

                <!-- Expandable details row -->
                <tr
                  v-if="expandedRows.includes(change.id)"
                  :key="`${change.id}-details`"
                  style="background: #f9f9f9;"
                >
                  <td
                    colspan="7"
                    style="padding: 20px; color: #363636 !important;"
                  >
                    <!-- Media information header -->
                    <div
                      v-if="
                        change.table_name === 'schedules' && change.media_info
                      "
                      style="margin-bottom: 15px; padding: 10px; background: white; border-left: 4px solid #48c774;"
                    >
                      <strong style="color: #363636 !important;"
                        >Media:
                      </strong>
                      <span style="color: #363636 !important;">{{
                        change.media_info.title
                      }}</span>
                      <span
                        v-if="
                          change.media_info.type === 'series' &&
                            change.media_info.series_no &&
                            change.media_info.episode_number
                        "
                        style="color: #7a7a7a !important;"
                      >
                        - Season {{ change.media_info.series_no }}, Episode
                        {{ change.media_info.episode_number }}
                      </span>
                      <br />
                      <span
                        v-if="change.media_info.episode_title"
                        style="color: #7a7a7a !important; font-size: 13px;"
                      >
                        Episode Title: {{ change.media_info.episode_title }}
                      </span>
                    </div>

                    <!-- Batch operation details -->
                    <div
                      v-if="change.table_name === 'batch' && change.enriched_info && change.enriched_info.is_batch"
                      style="color: #363636 !important;"
                    >
                      <!-- Batch Delete details -->
                      <div v-if="change.action === 'batch_delete'" style="display: flex; gap: 20px; margin-bottom: 15px;">
                        <div style="background: #f8d7da; padding: 10px 15px; border-radius: 4px; border-left: 4px solid #dc3545;">
                          <strong style="color: #721c24 !important;">Items Deleted</strong>
                          <div style="font-size: 24px; font-weight: bold; color: #721c24 !important;">
                            {{ change.enriched_info.total_count || change.enriched_info.item_count }}
                          </div>
                          <div v-if="change.enriched_info.child_count > 0" style="font-size: 11px; color: #721c24 !important;">
                            ({{ change.enriched_info.item_count }} items + {{ change.enriched_info.child_count }} children)
                          </div>
                        </div>
                        <div v-if="change.enriched_info.media_titles && change.enriched_info.media_titles.length > 0" style="background: #fff3cd; padding: 10px 15px; border-radius: 4px; border-left: 4px solid #ffc107;">
                          <strong style="color: #856404 !important;">Media Titles</strong>
                          <div style="font-size: 12px; color: #856404 !important;">
                            {{ change.enriched_info.media_titles.join(', ') }}
                            <span v-if="change.enriched_info.media_titles.length >= 5">...</span>
                          </div>
                        </div>
                      </div>

                      <!-- Batch Replace details -->
                      <div v-else-if="change.action === 'batch_replace'" style="display: flex; gap: 20px; margin-bottom: 15px;">
                        <div style="background: #d1ecf1; padding: 10px 15px; border-radius: 4px; border-left: 4px solid #17a2b8;">
                          <strong style="color: #0c5460 !important;">Items Replaced</strong>
                          <div style="font-size: 24px; font-weight: bold; color: #0c5460 !important;">
                            {{ change.enriched_info.item_count }}
                          </div>
                        </div>
                        <div style="background: #d4edda; padding: 10px 15px; border-radius: 4px; border-left: 4px solid #28a745;">
                          <strong style="color: #155724 !important;">New Media</strong>
                          <div style="font-size: 14px; color: #155724 !important; font-weight: bold;">
                            {{ (change.enriched_info.new_media && change.enriched_info.new_media.title) || 'Unknown' }}
                          </div>
                          <div v-if="change.enriched_info.new_media && change.enriched_info.new_media.duration" style="font-size: 11px; color: #155724 !important;">
                            Duration: {{ formatDuration(change.enriched_info.new_media.duration) }}
                          </div>
                        </div>
                      </div>
                      <div v-if="change.action === 'batch_replace' && change.enriched_info.old_media_titles && change.enriched_info.old_media_titles.length > 0" style="font-size: 12px; color: #7a7a7a !important; margin-bottom: 10px;">
                        <strong>Previous:</strong> {{ change.enriched_info.old_media_titles.join(', ') }}
                        <span v-if="change.enriched_info.old_media_titles.length >= 5">...</span>
                      </div>

                      <!-- Batch Copy details -->
                      <div v-else-if="change.action === 'batch_copy'" style="display: flex; gap: 20px; margin-bottom: 15px;">
                        <div style="background: #d4edda; padding: 10px 15px; border-radius: 4px; border-left: 4px solid #28a745;">
                          <strong style="color: #155724 !important;">Items Copied</strong>
                          <div style="font-size: 24px; font-weight: bold; color: #155724 !important;">
                            {{ change.enriched_info.item_count }}
                          </div>
                        </div>
                        <div style="background: #e2e3e5; padding: 10px 15px; border-radius: 4px; border-left: 4px solid #6c757d;">
                          <strong style="color: #383d41 !important;">Date Copy</strong>
                          <div style="font-size: 13px; color: #383d41 !important;">
                            {{ change.enriched_info.source_date }} <i class="mdi mdi-arrow-right"></i> {{ change.enriched_info.destination_date }}
                          </div>
                        </div>
                        <div v-if="change.enriched_info.skipped_count > 0" style="background: #fff3cd; padding: 10px 15px; border-radius: 4px; border-left: 4px solid #ffc107;">
                          <strong style="color: #856404 !important;">Skipped</strong>
                          <div style="font-size: 24px; font-weight: bold; color: #856404 !important;">
                            {{ change.enriched_info.skipped_count }}
                          </div>
                        </div>
                      </div>
                      <div v-if="change.action === 'batch_copy' && change.enriched_info.media_titles && change.enriched_info.media_titles.length > 0" style="font-size: 12px; color: #7a7a7a !important;">
                        <strong>Media:</strong> {{ change.enriched_info.media_titles.join(', ') }}
                        <span v-if="change.enriched_info.media_titles.length >= 5">...</span>
                      </div>
                    </div>

                    <!-- Sequence delete details -->
                    <div
                      v-else-if="change.action === 'sequence_delete' && change.enriched_info && change.enriched_info.is_sequence"
                      style="color: #363636 !important;"
                    >
                      <div style="display: flex; gap: 20px; margin-bottom: 15px;">
                        <div style="background: #fff3cd; padding: 10px 15px; border-radius: 4px; border-left: 4px solid #ffc107;">
                          <strong style="color: #856404 !important;">Episodes Deleted</strong>
                          <div style="font-size: 24px; font-weight: bold; color: #856404 !important;">
                            {{ change.enriched_info.parent_item_count }}
                          </div>
                        </div>
                        <div style="background: #f8d7da; padding: 10px 15px; border-radius: 4px; border-left: 4px solid #dc3545;">
                          <strong style="color: #721c24 !important;">Total Items</strong>
                          <div style="font-size: 24px; font-weight: bold; color: #721c24 !important;">
                            {{ change.enriched_info.total_item_count }}
                          </div>
                          <div style="font-size: 11px; color: #721c24 !important;">
                            ({{ change.enriched_info.parent_item_count }} episodes + {{ change.enriched_info.child_item_count }} segments/breaks)
                          </div>
                        </div>
                      </div>
                      <div v-if="change.enriched_info.first_start_time" style="font-size: 12px; color: #7a7a7a !important;">
                        <strong>Date Range:</strong>
                        {{ formatDateTime(change.enriched_info.first_start_time) }}
                        <span v-if="change.enriched_info.last_start_time !== change.enriched_info.first_start_time">
                          to {{ formatDateTime(change.enriched_info.last_start_time) }}
                        </span>
                      </div>
                    </div>

                    <!-- Sequence restore details -->
                    <div
                      v-else-if="change.action === 'sequence_restore' && change.new_values && change.new_values.sequence_info"
                      style="color: #363636 !important;"
                    >
                      <div style="background: #d4edda; padding: 15px; border-radius: 4px; border-left: 4px solid #28a745;">
                        <strong style="color: #155724 !important;">
                          <i class="mdi mdi-check-circle" style="margin-right: 6px;"></i>
                          Sequence Restored Successfully
                        </strong>
                        <div style="margin-top: 8px; color: #155724 !important;">
                          {{ change.new_values.sequence_info.parent_item_count }} episodes and
                          {{ change.new_values.sequence_info.child_item_count }} segments/breaks restored.
                        </div>
                      </div>
                    </div>

                    <div
                      v-else-if="change.action === 'update'"
                      style="color: #363636 !important;"
                    >
                      <strong style="color: #363636 !important;"
                        >Changed Fields:</strong
                      >
                      <table
                        class="table is-narrow is-fullwidth"
                        style="margin-top: 10px; background: white;"
                      >
                        <thead>
                          <tr>
                            <th style="color: #363636 !important;">Field</th>
                            <th style="color: #363636 !important;">
                              Old Value
                            </th>
                            <th style="color: #363636 !important;"></th>
                            <th style="color: #363636 !important;">
                              New Value
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(value, key) in change.old_values"
                            :key="key"
                          >
                            <td
                              class="has-text-weight-bold"
                              style="color: #363636 !important;"
                            >
                              {{ formatFieldName(key) }}
                            </td>
                            <td style="color: #f14668 !important;">
                              {{
                                formatValue(value, key, change.channel_timezone)
                              }}
                            </td>
                            <td
                              class="has-text-centered"
                              style="color: #363636 !important;"
                            >
                              <i class="mdi mdi-arrow-right"></i>
                            </td>
                            <td style="color: #48c774 !important;">
                              {{
                                formatValue(
                                  change.new_values[key],
                                  key,
                                  change.channel_timezone
                                )
                              }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div
                      v-else-if="change.action === 'delete'"
                      style="color: #363636 !important;"
                    >
                      <strong style="color: #363636 !important;"
                        >Deleted Data:</strong
                      >
                      <pre
                        style="margin-top: 10px; color: #363636 !important; background: #f5f5f5 !important; padding: 10px; border-radius: 4px;"
                        >{{
                          JSON.stringify(
                            formatJSONWithTimezone(
                              change.old_values,
                              change.channel_timezone
                            ),
                            null,
                            2
                          )
                        }}</pre
                      >
                    </div>
                    <div
                      v-else-if="change.action === 'create'"
                      style="color: #363636 !important;"
                    >
                      <strong style="color: #363636 !important;"
                        >Created Data:</strong
                      >
                      <pre
                        style="margin-top: 10px; color: #363636 !important; background: #f5f5f5 !important; padding: 10px; border-radius: 4px;"
                        >{{
                          JSON.stringify(
                            formatJSONWithTimezone(
                              change.new_values,
                              change.channel_timezone
                            ),
                            null,
                            2
                          )
                        }}</pre
                      >
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>

          <!-- OLD BUEFY TABLE - HIDDEN FOR NOW -->
          <b-table
            v-if="false"
            :data="changes"
            :striped="true"
            :hoverable="true"
            :mobile-cards="false"
            detailed
            detail-key="id"
            class="force-visible-table"
          >
            <b-table-column
              v-slot="props"
              field="created_at"
              label="Date/Time"
              width="180"
            >
              <span style="color: #363636 !important;">
                {{ formatDateTime(props.row.created_at) }}
              </span>
            </b-table-column>

            <b-table-column
              v-slot="props"
              field="user"
              label="User"
              width="200"
            >
              <div v-if="props.row.user" style="color: #363636 !important;">
                <div
                  class="has-text-weight-bold"
                  style="color: #363636 !important;"
                >
                  {{ props.row.user.name }}
                </div>
                <div
                  class="is-size-7 has-text-grey"
                  style="color: #7a7a7a !important;"
                >
                  {{ props.row.user.email }}
                </div>
              </div>
              <div
                v-else
                class="has-text-grey-light"
                style="color: #b5b5b5 !important;"
              >
                Unknown User
              </div>
            </b-table-column>

            <b-table-column
              v-slot="props"
              field="action"
              label="Action"
              width="100"
            >
              <b-tag :type="getActionType(props.row.action)" size="is-small">
                {{ props.row.action }}
              </b-tag>
            </b-table-column>

            <b-table-column
              v-slot="props"
              field="table_name"
              label="Type"
              width="120"
            >
              <span style="color: #363636 !important;">
                {{ formatTableName(props.row.table_name) }}
              </span>
            </b-table-column>

            <b-table-column v-slot="props" label="Summary">
              <div
                v-if="props.row.action === 'update'"
                class="is-size-7"
                style="color: #363636 !important;"
              >
                Updated
                {{ Object.keys(props.row.old_values || {}).length }} field(s)
              </div>
              <div
                v-else-if="props.row.action === 'delete'"
                class="is-size-7"
                style="color: #363636 !important;"
              >
                Deleted {{ formatTableName(props.row.table_name) }}
              </div>
              <div
                v-else-if="props.row.action === 'create'"
                class="is-size-7"
                style="color: #363636 !important;"
              >
                Created {{ formatTableName(props.row.table_name) }}
              </div>
            </b-table-column>

            <b-table-column v-slot="props" label="Actions" width="120">
              <b-button
                v-if="!props.row.reverted"
                type="is-warning"
                size="is-small"
                icon-left="undo"
                :loading="undoingId === props.row.id"
                @click="confirmUndo(props.row)"
              >
                Undo
              </b-button>
              <b-tag v-else type="is-light" size="is-small">
                Reverted
              </b-tag>
            </b-table-column>

            <template #detail="props">
              <div
                class="content"
                style="padding: 10px 20px; color: #363636 !important;"
              >
                <div
                  v-if="props.row.action === 'update'"
                  style="color: #363636 !important;"
                >
                  <strong style="color: #363636 !important;"
                    >Changed Fields:</strong
                  >
                  <table class="table is-narrow is-fullwidth has-margin-top-10">
                    <thead>
                      <tr>
                        <th style="color: #363636 !important;">Field</th>
                        <th style="color: #363636 !important;">Old Value</th>
                        <th style="color: #363636 !important;"></th>
                        <th style="color: #363636 !important;">New Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(value, key) in props.row.old_values"
                        :key="key"
                      >
                        <td
                          class="has-text-weight-bold"
                          style="color: #363636 !important;"
                        >
                          {{ formatFieldName(key) }}
                        </td>
                        <td
                          class="has-text-danger"
                          style="color: #f14668 !important;"
                        >
                          {{ formatValue(value) }}
                        </td>
                        <td
                          class="has-text-centered"
                          style="color: #363636 !important;"
                        >
                          <b-icon icon="arrow-right" size="is-small"></b-icon>
                        </td>
                        <td
                          class="has-text-success"
                          style="color: #48c774 !important;"
                        >
                          {{ formatValue(props.row.new_values[key]) }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div
                  v-else-if="props.row.action === 'delete'"
                  style="color: #363636 !important;"
                >
                  <strong style="color: #363636 !important;"
                    >Deleted Data:</strong
                  >
                  <pre
                    class="has-margin-top-10"
                    style="color: #363636 !important; background: #f5f5f5 !important;"
                    >{{ JSON.stringify(props.row.old_values, null, 2) }}</pre
                  >
                </div>
                <div
                  v-else-if="props.row.action === 'create'"
                  style="color: #363636 !important;"
                >
                  <strong style="color: #363636 !important;"
                    >Created Data:</strong
                  >
                  <pre
                    class="has-margin-top-10"
                    style="color: #363636 !important; background: #f5f5f5 !important;"
                    >{{ JSON.stringify(props.row.new_values, null, 2) }}</pre
                  >
                </div>
              </div>
            </template>
          </b-table>
        </div>
      </section>
      <footer class="modal-card-foot">
        <button class="button" type="button" @click="closeModal">
          Close
        </button>
        <button
          class="button is-primary"
          :loading="loading"
          @click="loadChanges"
        >
          Refresh
        </button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import AuditLogApi from '@/api/auditLog'
import format from 'date-fns/format'
import { formatInTimeZone } from 'date-fns-tz'

export default {
  name: 'RecentChangesModal',
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showModal: this.show,
      loading: false,
      changes: [],
      undoingId: null,
      expandedRows: []
    }
  },
  watch: {
    show() {
      this.showModal = this.show
      if (this.show) {
        this.loadChanges()
      }
    }
  },
  methods: {
    async loadChanges() {
      try {
        this.loading = true
        this.changes = await AuditLogApi.getRecentChanges(100)
        console.log('[RECENT CHANGES] Loaded changes:', this.changes)
        console.log('[RECENT CHANGES] Number of changes:', this.changes.length)
        if (this.changes.length > 0) {
          console.log('[RECENT CHANGES] First change:', this.changes[0])
        }
      } catch (error) {
        console.error('Failed to load audit history:', error)
        this.$buefy.toast.open({
          message: 'Failed to load change history',
          position: 'is-top',
          type: 'is-danger'
        })
      } finally {
        this.loading = false
      }
    },
    async confirmUndo(change) {
      this.$buefy.dialog.confirm({
        title: 'Undo Change',
        message: `Are you sure you want to undo this ${
          change.action
        }? This will revert the changes made on ${this.formatDateTime(
          change.created_at
        )}.`,
        confirmText: 'Undo',
        type: 'is-warning',
        hasIcon: true,
        icon: 'undo',
        onConfirm: () => this.undoChange(change.id)
      })
    },
    async undoChange(auditLogId) {
      try {
        this.undoingId = auditLogId
        await AuditLogApi.undoChange(auditLogId)
        this.$buefy.toast.open({
          message: 'Change undone successfully!',
          position: 'is-top',
          type: 'is-success'
        })
        this.loadChanges()
        this.$emit('refresh')
      } catch (error) {
        console.error('Failed to undo change:', error)
        this.$buefy.toast.open({
          message: (error && error.data && error.data.error) || 'Failed to undo change',
          position: 'is-top',
          type: 'is-danger'
        })
      } finally {
        this.undoingId = null
      }
    },
    formatDateTime(dateString) {
      return format(new Date(dateString), 'yyyy-MM-dd HH:mm:ss')
    },
    formatValue(value, fieldName = null, timezone = null) {
      if (value === null || value === undefined) return 'N/A'

      // Check if this is a datetime field that should use timezone
      const isDateTimeField =
        fieldName && (fieldName.includes('time') || fieldName.includes('date'))
      const shouldUseTimezone = isDateTimeField && timezone

      if (typeof value === 'object' && value instanceof Date) {
        if (shouldUseTimezone) {
          return formatInTimeZone(value, timezone, 'yyyy-MM-dd HH:mm')
        }
        return format(new Date(value), 'yyyy-MM-dd HH:mm')
      }

      if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
        if (shouldUseTimezone) {
          return formatInTimeZone(new Date(value), timezone, 'yyyy-MM-dd HH:mm')
        }
        return format(new Date(value), 'yyyy-MM-dd HH:mm')
      }

      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No'
      }

      // Handle JSON objects (mediadata, episodedata, etc.)
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value, null, 2)
      }

      return String(value)
    },
    formatFieldName(fieldName) {
      return fieldName
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    formatTableName(tableName) {
      if (tableName === 'schedules') return 'Schedule'
      if (tableName === 'media') return 'Media'
      if (tableName === 'channels') return 'Channel'
      if (tableName === 'sequence') return 'Sequence'
      if (tableName === 'batch') return 'Batch'
      return tableName.charAt(0).toUpperCase() + tableName.slice(1)
    },
    formatAction(action) {
      if (action === 'sequence_delete') return 'Sequence Delete'
      if (action === 'sequence_restore') return 'Sequence Restore'
      if (action === 'sequence_copy') return 'Sequence Copy'
      if (action === 'sequence_create') return 'Sequence Create'
      if (action === 'batch_delete') return 'Bulk Delete'
      if (action === 'batch_replace') return 'Bulk Replace'
      if (action === 'batch_copy') return 'Copy Playlist'
      if (action === 'batch_undo_delete') return 'Undo Delete'
      if (action === 'batch_undo_replace') return 'Undo Replace'
      if (action === 'batch_undo_copy') return 'Undo Copy'
      return action
    },
    formatDuration(seconds) {
      if (!seconds) return '0s'
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      if (mins > 0) {
        return `${mins}m ${secs}s`
      }
      return `${secs}s`
    },
    getActionType(action) {
      if (action === 'create') return 'is-success'
      if (action === 'update') return 'is-info'
      if (action === 'delete') return 'is-danger'
      return ''
    },
    closeModal() {
      this.showModal = false
      this.$emit('close-modal')
    },
    toggleDetails(changeId) {
      const index = this.expandedRows.indexOf(changeId)
      if (index > -1) {
        this.expandedRows.splice(index, 1)
      } else {
        this.expandedRows.push(changeId)
      }
    },
    formatJSONWithTimezone(obj, timezone) {
      if (!obj || !timezone) return obj

      const formatted = {}
      for (const [key, value] of Object.entries(obj)) {
        // Check if this is a datetime string
        if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
          // Convert UTC to channel timezone
          formatted[key] = formatInTimeZone(
            new Date(value),
            timezone,
            'yyyy-MM-dd HH:mm:ss'
          )
        } else {
          formatted[key] = value
        }
      }
      return formatted
    }
  }
}
</script>

<style>
/* Simple table styling */
.simple-table {
  width: 100%;
  border-collapse: collapse;
  background: white !important;
}

.simple-table thead tr {
  background: #f5f5f5 !important;
}

.simple-table th,
.simple-table td {
  padding: 12px 8px;
  text-align: left;
  color: #363636 !important;
  border-bottom: 1px solid #dbdbdb;
  vertical-align: middle;
}

.simple-table th {
  font-weight: 600;
  font-size: 14px;
}

.simple-table tbody tr:hover {
  background: #fafafa !important;
}

.simple-table strong {
  color: #363636 !important;
  font-weight: 600;
}

.simple-table small {
  font-size: 12px;
}

.simple-table .button {
  color: white !important;
}

/* CRITICAL: Force all text visibility in Recent Changes modal */
.modal-card-body {
  color: #363636 !important;
  background: white !important;
}

.modal-card-body * {
  color: #363636 !important;
}

/* Force visibility for this specific table */
.force-visible-table {
  background: white !important;
}

.force-visible-table td,
.force-visible-table th,
.force-visible-table tr {
  color: #363636 !important;
  background: white !important;
}

.force-visible-table .table {
  color: #363636 !important;
  background: white !important;
}

.force-visible-table .table td,
.force-visible-table .table th {
  color: #363636 !important;
  background: white !important;
}

.modal-card-body .table {
  color: #363636 !important;
}

.modal-card-body .table td,
.modal-card-body .table th {
  color: #363636 !important;
}

/* Buefy table specific - CRITICAL for visibility */
.modal-card-body .b-table,
.modal-card-body .b-table .table-wrapper,
.modal-card-body .b-table .table-wrapper .table {
  color: #363636 !important;
}

.modal-card-body .b-table .table tbody tr,
.modal-card-body .b-table .table tbody tr td {
  color: #363636 !important;
}

.modal-card-body .b-table .table thead tr,
.modal-card-body .b-table .table thead tr th {
  color: #363636 !important;
}

.modal-card-body .b-table .table tbody tr:hover,
.modal-card-body .b-table .table tbody tr:hover td {
  color: #363636 !important;
}

/* Override Buefy tags to keep white text on colored backgrounds */
.modal-card-body .tag {
  color: white !important;
}

.modal-card-body .tag.is-success,
.modal-card-body .tag.is-info,
.modal-card-body .tag.is-danger,
.modal-card-body .tag.is-warning {
  color: white !important;
}

/* Utility classes */
.modal-card-body .has-text-grey {
  color: #7a7a7a !important;
}

.modal-card-body .has-text-grey-light {
  color: #b5b5b5 !important;
}

.modal-card-body .has-text-weight-bold {
  color: #363636 !important;
}

.modal-card-body .is-size-7 {
  color: #363636 !important;
}

.modal-card-body .has-text-danger {
  color: #f14668 !important;
}

.modal-card-body .has-text-success {
  color: #48c774 !important;
}

/* Buttons should have proper contrast */
.modal-card-body .button {
  color: #363636 !important;
}

.modal-card-body .button.is-warning,
.modal-card-body .button.is-primary,
.modal-card-body .button.is-danger,
.modal-card-body .button.is-info,
.modal-card-body .button.is-success {
  color: white !important;
}

/* Pre tags for JSON display */
.modal-card-body pre {
  color: #363636 !important;
}

.modal-card-body strong {
  color: #363636 !important;
}

/* Content areas */
.modal-card-body .content,
.modal-card-body .content p,
.modal-card-body .content div,
.modal-card-body .content span {
  color: #363636 !important;
}
</style>
